import {
  collection,
  getDocs,
  onSnapshot,
  query,
  Unsubscribe,
  where,
} from "firebase/firestore";
import { isSubmittedComparisonDocument, isSubmittedComparisonRevision } from "~/utils/document-comparison";

export const useDocumentToCompare = (documentId: string) =>
  defineStore(`/docuemnt/compare/${documentId}`, () => {
    const documentIdToCompare = ref<string | undefined>(undefined);
    const hasDocumentToCompare = computed(
      () => documentIdToCompare.value != undefined
    );
    const setDocumentToCompare = (docId: string | undefined) => {
      documentIdToCompare.value = docId;
    };

    const documentsToCompare = ref<SubmittedDocuments>([]);

    const isInitialized = ref<boolean>(false);
    const subscription = ref<Unsubscribe | undefined>();
    let snapshotVersion = 0;
    const initialize = async () => {
      if (isInitialized.value) return;

      const documentStore = useSubmittedDocumentStore(documentId);
      const { submittedDocument } = storeToRefs(documentStore);

      if (submittedDocument.value == undefined) {
        canCompareDocuments.value = false;
        return;
      }

      const studentId = submittedDocument.value.studentId;
      const classroomId = submittedDocument.value.classroomId;

      if (classroomId == undefined) {
        canCompareDocuments.value = false;
        return;
      }

      const userId = useCurrentUID();

      if (userId == undefined) {
        canCompareDocuments.value = false;
        return;
      }

      const db = useFirestore();

      const documentsRef = collection(db, "/documents");
      let documentsQuery = query(
        documentsRef,
        where("classroomId", "==", classroomId)
      );

      if (studentId != undefined) {
        documentsQuery = query(
          documentsQuery,
          where("studentId", "==", studentId)
        );
      }

      if (subscription.value) {
        subscription.value();
      }

      subscription.value = onSnapshot(
        documentsQuery,
        async (snapshot) => {
          const version = ++snapshotVersion;
          const candidates = snapshot.docs
            .map((doc) => {
              return {
                ...doc.data(),
                id: doc.id,
              } as SubmittedDocument;
            })
            .filter((document) => document.id !== documentId && isSubmittedComparisonDocument(document));

          // Hide drafts immediately, including a selected submission withdrawn
          // while the comparison panel is open.
          const candidateIds = new Set(candidates.map((document) => document.id));
          documentsToCompare.value = documentsToCompare.value.filter((document) => candidateIds.has(document.id));
          if (documentIdToCompare.value && !candidateIds.has(documentIdToCompare.value)) {
            documentIdToCompare.value = undefined;
          }

          const results = await Promise.allSettled(candidates.map(async (document) => {
            const revisions = await getDocs(collection(db, `/documents/${document.id}/revisions`));
            return revisions.docs.some((revision) => isSubmittedComparisonRevision(revision.data()))
              ? document
              : undefined;
          }));
          if (version !== snapshotVersion) return;

          documentsToCompare.value = results.flatMap((result) =>
            result.status === "fulfilled" && result.value ? [result.value] : []
          );
          if (documentIdToCompare.value && !documentsToCompare.value.some((document) => document.id === documentIdToCompare.value)) {
            documentIdToCompare.value = undefined;
          }
        },
        (error) => {}
      );

      isInitialized.value = true;
    };

    const canCompareDocuments = ref(true);

    watch(documentsToCompare, (documents) => {
      canCompareDocuments.value = documents.length > 0;
    });

    return {
      documentIdToCompare,
      hasDocumentToCompare,
      canCompareDocuments,
      setDocumentToCompare,

      documentsToCompare,

      initialize,
    };
  });
